"use client";

import { useMemo, useState } from 'react'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import { COLORED_GROUPS, groupColor, OTHER_COLOR } from '@/lib/finance/categories'
import { fmtUsd, fmtPct } from '@/lib/finance/format'
import { ChartTooltip } from '@/components/finance/ChartTooltip'
import { ScopeMenu } from '@/components/finance/ui'

export type DonutSlice = { name: string; value: number; color: string }

const NOISE = 0.005

/**
 * Shapes a `byGroup` map into the donut's pie arcs and legend rows.
 *
 * A group's net value can be negative when refunds/reimbursements outweigh the
 * period's spend (e.g. a big Health & Fitness credit). A pie can't draw a
 * negative arc, so those credits are kept OUT of `slices` (the arcs) but IN
 * `legend` — so the breakdown list still surfaces them (and stays clickable to
 * drill into the underlying transactions). `positiveTotal` is the donut's whole
 * (sum of the drawn arcs); with no credits it equals the plain sum, so the
 * common case is unchanged.
 */
export function buildDonutModel(
  byGroup: Record<string, number>,
  fold: boolean,
  colorFor?: (name: string, index: number) => string,
): { slices: DonutSlice[]; legend: DonutSlice[]; positiveTotal: number } {
  // Keep both spends (>0) and credits (<0); drop only near-zero noise.
  const entries = Object.entries(byGroup).filter(([, v]) => Math.abs(v) > NOISE)

  let items: DonutSlice[]
  if (!fold) {
    items = entries
      .sort((a, b) => b[1] - a[1])
      .map(([g, v], i) => ({ name: g, value: v, color: colorFor ? colorFor(g, i) : groupColor(g) }))
  } else {
    const colored = entries.filter(([g]) => COLORED_GROUPS.includes(g as never))
    const rest = entries.filter(([g]) => !COLORED_GROUPS.includes(g as never))
    const restTotal = rest.reduce((s, [, v]) => s + v, 0)
    items = colored.map(([g, v]) => ({ name: g, value: v, color: groupColor(g) }))
    if (Math.abs(restTotal) > NOISE) items.push({ name: 'Other', value: restTotal, color: OTHER_COLOR })
  }

  // Spends first (largest → smallest); credits (negatives) fall to the end.
  const legend = [...items].sort((a, b) => b.value - a.value)
  const slices = legend.filter((s) => s.value > 0)
  const positiveTotal = slices.reduce((s, x) => s + x.value, 0)
  return { slices, legend, positiveTotal }
}

/**
 * Part-to-whole donut of spending by group. The 8 colored groups keep their
 * fixed hues; everything else folds into a gray "Other" slice (itemized in
 * the legend list beside/beneath the donut, so no value is color-gated).
 * 2px surface-color stroke = the surface gap between slices. Groups whose net
 * is a credit (negative) can't be drawn as an arc, so they're listed below the
 * spends in the legend rather than dropped.
 */
export function SpendingDonut({
  byGroup,
  onSelect,
  onScope,
  fold = true,
  colorFor,
  label = 'Spending',
}: {
  byGroup: Record<string, number>
  onSelect?: (name: string) => void
  /** When set, each legend row gets a hover popover to narrow ("only this") or
   * drop ("exclude") the item from the category scope. */
  onScope?: (name: string, mode: 'only' | 'exclude') => void
  /** Fold groups outside the fixed palette into a gray "Other" slice. Turn off
   * for category-level drilldowns, where every slice is shown and colored via
   * `colorFor`. */
  fold?: boolean
  /** Per-slice color override (rank-ordered), e.g. shades of a parent color. */
  colorFor?: (name: string, index: number) => string
  /** What the donut measures — tooltip header + empty state ("Income"). */
  label?: string
}) {
  const [active, setActive] = useState<string | null>(null)

  const { slices, legend, positiveTotal } = useMemo(
    () => buildDonutModel(byGroup, fold, colorFor),
    [byGroup, fold, colorFor],
  )

  if (!legend.length) return <p className="text-sm text-ink-3">No {label.toLowerCase()} in this period.</p>

  // A credit row hovered shouldn't dim the whole donut — only dim arcs when the
  // hovered name is actually drawn as a slice.
  const activeIsSlice = active != null && slices.some((s) => s.name === active)

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row">
      <div className="relative h-44 w-44 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip
              content={
                <ChartTooltip
                  labelFormatter={() => label}
                  valueFormatter={fmtUsd}
                />
              }
            />
            <Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              innerRadius="62%"
              outerRadius="100%"
              stroke="var(--surface-1)"
              strokeWidth={2}
              paddingAngle={0}
              // Hovering a slice re-renders (active-opacity); with animation on,
              // Recharts restarts the sweep each time, so it visibly "pauses".
              // Disable it — the donut appears instantly and hover stays smooth.
              isAnimationActive={false}
              onMouseEnter={(_, i) => setActive(slices[i].name)}
              onMouseLeave={() => setActive(null)}
              onClick={(_, i) => onSelect?.(slices[i].name)}
            >
              {slices.map((s) => (
                <Cell
                  key={s.name}
                  fill={s.color}
                  opacity={activeIsSlice && active !== s.name ? 0.45 : 1}
                  cursor={onSelect ? 'pointer' : undefined}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-lg font-semibold">{fmtUsd(positiveTotal)}</div>
          <div className="text-[10px] text-ink-3">total</div>
        </div>
      </div>

      {/* Legend + values: every group readable without hover. Credits (negative
          net) show below the spends with their signed amount and no percentage. */}
      <ul className="w-full min-w-0 flex-1 space-y-1">
        {legend.map((s) => {
          const row = (
            <button
              className={`flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1 text-left text-xs transition-colors hover:bg-surface-3/60 ${
                active === s.name ? 'bg-surface-3/60' : ''
              }`}
              onMouseEnter={() => setActive(s.name)}
              onMouseLeave={() => setActive(null)}
              onClick={() => onSelect?.(s.name)}
            >
              <span className="inline-flex min-w-0 items-center gap-1.5 text-ink-2">
                <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: s.color }} />
                <span className="truncate">{s.name}</span>
              </span>
              <span className="flex shrink-0 items-baseline gap-2 tabular-nums">
                <span className={`w-16 text-right ${s.value < 0 ? 'text-pos' : 'text-ink'}`}>{fmtUsd(s.value)}</span>
                <span className="w-8 text-right text-ink-3">
                  {s.value > 0 && positiveTotal > 0 ? fmtPct(s.value / positiveTotal, 0) : '—'}
                </span>
              </span>
            </button>
          )
          return (
            <li key={s.name}>
              {onScope ? (
                <ScopeMenu
                  className="relative flex w-full"
                  onOnly={() => onScope(s.name, 'only')}
                  onExclude={() => onScope(s.name, 'exclude')}
                >
                  {row}
                </ScopeMenu>
              ) : (
                row
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
